import React, { useState, useEffect } from "react";

import codingGif from "./icons/coding.gif";
import neuralGif from "./icons/neural-network.gif";
import savingGif from "./icons/data-processing.gif";
import successGif from "./icons/virtual-assistant.gif";

import CheckPicker from "rsuite/CheckPicker";
import "rsuite/CheckPicker/styles/index.css";

const Train = ({ initialUrl = "", refreshDocuments, agents = [], documentAgent }) => {

  console.log("Train component rendered with initialUrl:", initialUrl);

  const [step, setStep] = useState(1);
  const [url, setUrl] = useState("");
  const [documentId, setDocumentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(false); // ✅ Toggle to false for original behavior
  const [totalCount, setTotalCount] = useState(0);
  const [processedChunks, setProcessedChunks] = useState(0);
  const [progress, setProgress] = useState(0);

  const [selectedAgents, setSelectedAgents] = useState([]);

  const stepDetails = {
    1: { text: "Extracting Data", gif: codingGif },
    2: { text: "Generating Embeddings", gif: neuralGif },
    3: { text: "Saving to ChromaDB", gif: savingGif },
    4: { text: "Success!", gif: successGif },
  };

  const resetToFirstStep = () => {
    setStep(1);
    setUrl("");
    setDocumentId(null);
    setError(null);
    setTotalCount(0);
    setProcessedChunks(0);
    setProgress(0);
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleNext = async (inputUrl) => {

    if (!inputUrl) {
      return;
    }

    if (loading) {
      setError("Please wait until the current training is completed.");
      return;
    }

    if (!inputUrl) {
      setError("Document URL is required.");
      return;
    }

    setLoading(true);
    setError(null);
    setStep(1);

    if (preview) {
      setTimeout(() => {
        setLoading(false);
      }, 10000000);
      return;
    }

    try {
      if (step === 1) {

        const response = await fetch(`${process.env.REACT_APP_CRM}/ai/document/parsing`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            document_url: inputUrl,
            agents: selectedAgents,   // ✅ include selected agents
          }),
        });

        console.log("Response from document-content API:", response);

        const result = await response.json();

        console.log(result);

        if (!response.ok || !result.successful_processes) {
          throw new Error(result.error || "Failed to add document to queue");
        }

        const success = result.successful_processes;

        console.log('successResponse', success);

        // setStep(4);
        // refreshDocuments(); // Call the refreshDocuments function passed from parent component
        // setError(null);

        const count = success.total_chunks || 0;

        console.log("Total Chunks:", count);

        if (count === 0) {
          throw new Error("No chunks to process.");
        }

        setDocumentId(success.document_id);
        setTotalCount(count);
        setProcessedChunks(0);
        setProgress(0);
        setStep(2);

        await processEmbeddings(success.document_id, count, selectedAgents);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const processEmbeddings = async (docId, totalCount, selectedAgents) => {
    setLoading(true);
    try {
      let hasMore = true;

      while (hasMore) {
        //const response = await fetch(`${process.env.REACT_APP_CRM}/ai/get-embeddings?document_id=${docId}`);

        const response = await fetch(`${process.env.REACT_APP_CRM}/ai/document/embedding`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            document_id: docId,
            agents: selectedAgents,   // ✅ include selected agents
          }),
        });

        const result = await response.json();

        console.log(result);

        if (!response.ok || !result.successful_processes) {
          throw new Error(result.error || "Failed to process embedding");
        }

        const success = result.successful_processes;

        console.log('successResponse', success);

        hasMore = success.has_more;

        const remaining = success.count || 0;
        const processed = totalCount - remaining;

        setProcessedChunks(processed);
        const newProgress = Math.round((processed / totalCount) * 100);
        setProgress(newProgress);

        console.log("Processed Chunks:", processed, "/", totalCount);
        console.log("Progress %:", newProgress);

        await sleep(200); // smoothen progress bar
      }

      setStep(4);
      refreshDocuments(); // Call the refreshDocuments function passed from parent component
      setError(null);
      //await processSaving(docId);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const processSaving = async (docId) => {
    setLoading(true);
    try {
      let hasMore = true;
      while (hasMore) {

        const response = await fetch(`${process.env.REACT_APP_CRM}/ai/save?document_id=${docId}`);
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || "Failed to save data");

        hasMore = result.has_more;
      }
      setStep(4);

      refreshDocuments(); // Call the refreshDocuments function passed from parent component
      setError(null);

    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewNextStep = () => {
    if (step < 4) {
      setStep((prev) => prev + 1);
    }
  };

  useEffect(() => {
    if (initialUrl) {

      console.log("Initial URL provided:", initialUrl);
      // If initial data is passed from parent, start training automatically
      setUrl(initialUrl);

      //handleNext(initialUrl); // Start the process
      setError(null);
      // setStep(1); // move directly to Step 2 (Generating Embeddings)

      // setLoading(true);
      // processEmbeddings(initialDocumentId, 0); // totalCount=0 for now, will adjust inside processEmbeddings
    }
  }, [initialUrl]);

  useEffect(() => {
    if (documentAgent && documentAgent.length > 0) {
      setSelectedAgents(documentAgent);
    } else {
      setSelectedAgents([]); // reset when new doc
    }
  }, [documentAgent]);


  return (

    <div
      className="modal fade"
      id="documentModal"
      tabIndex="-1"
      role="dialog"
      aria-labelledby="documentModalLabel"
    >
      <div className="modal-dialog" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <button
              type="button"
              className="close"
              data-dismiss="modal"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
            {/* <h4 className="modal-title" id="documentModalLabel">Add Document</h4> */}

            <h4 className="modal-title" id="documentModalLabel">
              {loading ? stepDetails[step].text : "Add Document"}
            </h4>
          </div>

          <div className="modal-body">
            {error && (
              <div>
                <p className="text-danger">Error: {error}</p>
              </div>
            )}

            {step === 1 && (
              <div className="text-center">
                {loading ? (
                  <div>
                    <img
                      src={stepDetails[step].gif}
                      alt={stepDetails[step].text}
                      style={{ width: "150px", height: "150px" }}
                    />
                    {/* <p>{stepDetails[step].text}...</p> */}
                    <p className="text-warning mt-2">
                      🔒 Please do not close this modal or refresh the page while processing.
                    </p>
                  </div>
                ) : (

                  <div className="text-center">
                    <div>
                      <div className="form-group" style={{ marginTop: '10px', paddingBottom: '0px' }}>
                        <CheckPicker
                          data={agents.map((agent) => ({
                            label: agent.agent_name,
                            value: agent.agent_id,
                          }))}
                          value={selectedAgents}
                          searchable={false}
                          onChange={(values) => setSelectedAgents(values || [])}
                          placeholder="Select Agents"
                          style={{ width: "100%" }}
                          cleanable
                        />
                      </div>
                      <div className="form-group" style={{ marginTop: '10px' }}>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter a URL"
                          autoComplete="off"
                          value={url}
                          onChange={(e) => setUrl(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}


            {(step === 2 || step === 3) && (
              <div>
                <div className="text-center">
                  <img
                    src={stepDetails[step].gif}
                    alt={stepDetails[step].text}
                    style={{ width: "150px", height: "150px" }}
                  />
                  {/* <p>{stepDetails[step].text}...</p> */}
                  <p className="text-warning mt-2">
                    🔒 Please do not close this modal or refresh the page while processing.
                  </p>
                </div>

                <div className="progress mt-10">
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: `${progress}%` }}
                    aria-valuenow={progress}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  >
                    {progress}%
                  </div>
                </div>
                <p>
                  {processedChunks} of {totalCount} chunks processed
                </p>

              </div>

            )}

            {step === 4 && (
              <div className="text-center">
                <img
                  src={stepDetails[step].gif}
                  alt="Success"
                  style={{ width: "150px", height: "150px" }}
                />
              </div>
            )}
          </div>


          <div className="modal-footer">
            {step === 1 && !loading && (
              <div>
                <button
                  className="btn btn-blue mob-btn"
                  data-dismiss="modal"
                  onClick={resetToFirstStep}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-sky"
                  onClick={() => handleNext(url)}
                  disabled={!url || loading || selectedAgents.length === 0}
                >
                  Train
                </button>
              </div>
            )}

            {step === 4 && (
              <div>
                <a
                  className="btn btn-sky"
                  href="/ask"
                >
                  Ask Alie
                </a>
                <button
                  className="btn btn-blue mob-btn"
                  data-dismiss="modal"
                  onClick={resetToFirstStep}
                >
                  Done
                </button>
              </div>

            )}
          </div>
        </div>
      </div>
    </div>

  );
};

export default Train;



// WEBPACK FOOTER //
// ./src/components/ai/train.js